import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'

export type Profile = Database['public']['Tables']['profiles']['Row']

export interface AdminInvitationEmbed {
  note: string | null
  accepted_at: string | null
  expires_at: string
}

export type AdminUserListItem = Pick<
  Profile,
  'id' | 'email' | 'full_name' | 'is_super_admin' | 'created_at'
> & {
  /** Distinct non-empty notes from the user's invitations (e.g. bulk cohort labels). */
  invite_notes: string[]
}

export const profile = {
  findOne: (userId: string) =>
    queryOptions({
      queryKey: ['profile', userId],
      queryFn: async () => {
        if (!userId || userId.trim() === '') {
          throw new Error(
            'Invalid userId: cannot fetch profile without valid user ID'
          )
        }

        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single()

        if (error) {
          throw new Error(`Failed to fetch profile: ${error.message}`, {
            cause: error,
          })
        }

        if (!data) {
          throw new Error('Profile not found')
        }

        return data as Profile
      },
    }),

  // Super-admin only: lists every profile (RLS "Super admins can read any
  // profile" authorizes the cross-tenant read).
  findManyForAdmin: () =>
    queryOptions({
      queryKey: ['admin', 'profiles'],
      queryFn: async (): Promise<AdminUserListItem[]> => {
        const { data, error } = await supabase
          .from('profiles')
          .select(
            'id, email, full_name, is_super_admin, created_at, organization_invitations!user_id(note, accepted_at, expires_at)'
          )
          .order('created_at', { ascending: false })
          .order('id', { ascending: true })

        if (error) {
          throw new Error(`Failed to fetch profiles: ${error.message}`, {
            cause: error,
          })
        }

        return (data ?? []).map(({ organization_invitations, ...row }) => {
          const invitations = organization_invitations ?? []
          return {
            ...row,
            invite_notes: [
              ...new Set(
                invitations
                  .map((invitation) => invitation.note?.trim())
                  .filter((note): note is string => !!note)
              ),
            ],
          }
        })
      },
      staleTime: 5 * 60 * 1000,
    }),
}
